import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { userSelector } from 'store/selectors/app-selectors';
import { UserLoginAsModal } from 'features/header/header-top-panel/user-section/user-loginas-dialog/user-loginas-dialog';
import { UserExitLoginAsModal } from 'features/header/header-top-panel/user-section/user-loginas-dialog/user-exit-loginas-dialog';
import {
    BodyText,
    Divider,
    FlexContainer,
    Icon,
    IconButton,
    Link,
    MultilineDropdownMenu,
    PopoverTemplate,
    SimpleTooltip,
    Thumbnail,
} from '@placer-ui/components';
import { getFeatureFlags } from 'core/flow-control';
import { impersonateUserCookieStorage } from 'core/authentification';
import { goTo, goToInANewTab } from 'router/go-to';
import { PLACER_URL } from 'shared/constants/url';
import { useChangeUserInLoginasMode } from 'features/header/header-top-panel/user-section/user-loginas-dialog/utils';
import { useAuthActions } from 'store/auth/use-auth-actions';
import { useSegmentCallback } from 'hooks/use-segment-callback/use-segment-callback';

import styles from './user-section-box.module.scss';

const PLACER_PRIVACY_POLICY_LINK = 'privacy-policy/platform-services-privacy-policy';

export const UserSectionBox = () => {
    const [impersonateModalOpen, setImpersonateModalOpen] = useState(false);
    const [exitImpersonateModeModalOpen, setExitImpersonateModeModalOpen] = useState(false);
    const [isBoxOpen, setIsBoxOpen] = useState(false);
    const user = useSelector(userSelector);
    const { enable_impersonate } = getFeatureFlags();

    const changeUserInLoginAsMode = useChangeUserInLoginasMode();
    const { logout } = useAuthActions();
    const { track } = useSegmentCallback();

    const originalUser = impersonateUserCookieStorage.user;
    // if is an imporsonated user already then it means client has permissions to further impersonate
    const canImpersonate = enable_impersonate || originalUser;

    const userName = user?.fullName;
    const userEmail = user?.email;
    const userAccount = user?.settings?.account?.name;
    const originalUserEmail = originalUser?.email;
    const userLogo = user?.settings?.account?.logo_img;

    const hasThreeLines = (userName || originalUserEmail) && userEmail && userAccount;

    const onPrivacyPolicyClick = () => {
        goToInANewTab(PLACER_PRIVACY_POLICY_LINK, PLACER_URL);
    };

    const onLoginAsModalClose = () => {
        setImpersonateModalOpen(false);
    };

    const onSettingsClick = () => {
        setIsBoxOpen(false);
        goTo('/settings');
    };

    const onLogoutClick = () => {
        setIsBoxOpen(false);
        track({
            category: 'User',
            action: 'Signed Out',
        });
        logout();
    };

    const onLoginAsClick = () => {
        setIsBoxOpen(false);
        setImpersonateModalOpen(true);
    };

    const onExitLoginAsMode = () => {
        setExitImpersonateModeModalOpen(true);
        if (originalUser) {
            changeUserInLoginAsMode(originalUser, true);
        }
    };

    return (
        <>
            <PopoverTemplate
                onOpenChange={setIsBoxOpen}
                open={isBoxOpen}
                backgroundColor="white"
                testId="user-settings-menu"
                align="end"
                maxWidth={300}
                header={
                    <div className={styles.boxHeader}>
                        <Thumbnail
                            size={hasThreeLines ? 'extra-large' : 'large'}
                            src={userLogo || <Icon type="BuildingOffice" />}
                            hasBackground={!userLogo}
                            testId="user-thumbnail"
                        />
                        <FlexContainer flexDirection="column" maxWidth="100%">
                            {userName && (
                                <SimpleTooltip title={userName} testId="user-name-tooltip">
                                    <button
                                        className={styles.textWrapper}
                                        data-testid="user-name-button"
                                        tabIndex={0}
                                    >
                                        <BodyText variant="bold" isEllipsis>
                                            {userName}
                                        </BodyText>
                                    </button>
                                </SimpleTooltip>
                            )}
                            {userEmail && (
                                <SimpleTooltip title={userEmail} testId="user-email-tooltip">
                                    <button
                                        className={styles.textWrapper}
                                        data-testid="user-email-button"
                                    >
                                        <BodyText variant="semi-bold" isEllipsis>
                                            {userEmail}
                                        </BodyText>
                                    </button>
                                </SimpleTooltip>
                            )}
                            {userAccount && (
                                <SimpleTooltip title={userAccount} testId="user-account-tooltip">
                                    <button
                                        className={styles.textWrapper}
                                        data-testid="user-account-button"
                                    >
                                        <BodyText isEllipsis>{userAccount}</BodyText>
                                    </button>
                                </SimpleTooltip>
                            )}
                            {originalUserEmail && (
                                <SimpleTooltip
                                    title={originalUserEmail}
                                    testId="original-user-email-tooltip"
                                >
                                    <button
                                        className={styles.textWrapper}
                                        data-testid="original-user-email-button"
                                    >
                                        <BodyText isEllipsis>{originalUserEmail}</BodyText>
                                    </button>
                                </SimpleTooltip>
                            )}
                        </FlexContainer>
                    </div>
                }
                trigger={
                    <IconButton
                        size="small"
                        variant="ghost"
                        aria-label="user section"
                        Icon={<Icon type="User" size={24} />}
                        testId="user-section-icon-button"
                    />
                }
            >
                <FlexContainer flexDirection="column">
                    <FlexContainer flexDirection="column" width={300}>
                        <MultilineDropdownMenu
                            isUsingNewIconInfra
                            items={[
                                {
                                    label: 'Account & User Settings',
                                    Icon: <Icon type="Gear" size={24} />,
                                    value: 'account-settings',
                                    testId: 'user-account-settings',
                                    onClick: onSettingsClick,
                                },
                                {
                                    label: 'Log Out',
                                    Icon: <Icon type="SignOut" size={24} />,
                                    value: 'logout',
                                    testId: 'user-logout-button',
                                    onClick: onLogoutClick,
                                },
                                ...(canImpersonate
                                    ? [
                                          {
                                              label: 'Login As...',
                                              Icon: <Icon type="UserSwitch" size={24} />,
                                              value: 'login-as',
                                              testId: 'user-login-as-button',
                                              onClick: onLoginAsClick,
                                          },
                                      ]
                                    : []),
                                ...(originalUser
                                    ? [
                                          {
                                              label: "Exit 'Login As'",
                                              Icon: <Icon type="SignOut" size={24} />,
                                              value: 'exit-login-as',
                                              testId: 'user-exit-login-as-button',
                                              onClick: onExitLoginAsMode,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                        <Divider />
                    </FlexContainer>
                    <FlexContainer padding={16} justifyContent="center">
                        <Link
                            onClick={onPrivacyPolicyClick}
                            color="gray"
                            testId="user-privacy-policy-link"
                            weight="semi-bold"
                        >
                            Privacy Policy
                        </Link>
                    </FlexContainer>
                </FlexContainer>
            </PopoverTemplate>
            {impersonateModalOpen && (
                <UserLoginAsModal visible={impersonateModalOpen} onClose={onLoginAsModalClose} />
            )}
            {exitImpersonateModeModalOpen && (
                <UserExitLoginAsModal visible={exitImpersonateModeModalOpen} />
            )}
        </>
    );
};
